import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../components/core/card';
import type { AbilityCheck, AbilityScore } from './types';

const SECTION_CARD_CLASS =
  'rounded-xl border border-rose-200 border-b-[4px] border-b-rose-300 bg-gradient-to-b from-white to-gray-50/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_4px_0_rgba(0,0,0,0.05),0_6px_12px_rgba(0,0,0,0.04)]';

// Ring colors reused from StatRing.
const SCORE_COLORS: Record<AbilityScore, string> = {
  yes: '#4aba6a',
  partially: '#f0a030',
  not_yet: '#e24b4a',
};

const SCORE_ORDER: AbilityScore[] = ['not_yet', 'partially', 'yes'];

const SCORE_LABEL_KEYS: Record<AbilityScore, string> = {
  not_yet: 'studentFeedback.abilityScore.notYet',
  partially: 'studentFeedback.abilityScore.partially',
  yes: 'studentFeedback.abilityScore.yes',
};

interface AbilityCheckCardProps {
  abilityChecks: AbilityCheck[];
  editable?: boolean;
  onChange?: (index: number, score: AbilityScore) => void;
}

export function AbilityCheckCard({ abilityChecks, editable = false, onChange }: AbilityCheckCardProps) {
  const { t } = useTranslation('admin');

  return (
    <Card className={SECTION_CARD_CLASS}>
      <CardContent className="divide-y divide-rose-100 p-0">
        {abilityChecks.map((ability, index) => (
          <div
            key={ability.abilityId || index}
            className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
          >
            <p className="flex-1 text-sm leading-relaxed text-gray-700">{ability.text}</p>
            <div className="inline-flex flex-shrink-0 overflow-hidden rounded-lg border border-gray-200">
              {SCORE_ORDER.map((score, position) => {
                const isSelected = ability.score === score;
                const color = SCORE_COLORS[score];
                const label = t(SCORE_LABEL_KEYS[score]);
                const baseClass = 'px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none';
                const borderClass = position > 0 ? 'border-l border-gray-200' : '';

                if (editable) {
                  return (
                    <button
                      key={score}
                      type="button"
                      onClick={() => onChange?.(index, score)}
                      aria-pressed={isSelected}
                      className={`${baseClass} ${borderClass} ${isSelected ? 'text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                      style={isSelected ? { backgroundColor: color } : undefined}
                    >
                      {label}
                    </button>
                  );
                }

                return (
                  <span
                    key={score}
                    className={`${baseClass} ${borderClass} ${isSelected ? 'text-white' : 'text-gray-300'}`}
                    style={isSelected ? { backgroundColor: color } : undefined}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
